import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AppLocale } from "@akina/infra";
import { AvailableLocales, DEFAULT_LOCALE } from "../../infra/locales";
import { sharedEnvironment } from "../../config";
import type { AppName } from "../../config/environmentService";

type LocaleStore = {
  locale: AppLocale;
  setLocale: (newLocale: AppLocale) => void;
}

const SUPPORTED_LOCALES_BY_APP: Record<AppName, readonly AppLocale[]> = {
  care: AvailableLocales,
  portal: AvailableLocales,
  webshop: ["en-CH", "de"],
};

export const getLocaleFromNavigator = (): AppLocale => {
  try {
    const l = navigator?.language;
    if (!l || l === "en") return DEFAULT_LOCALE;
    return l as AppLocale;
  } catch {
    return DEFAULT_LOCALE;
  }
};

export const parseDefaultLocale = (
  locale: AppLocale,
  supportedLocales: readonly AppLocale[] = AvailableLocales,
): AppLocale => {
  let parsed: AppLocale = DEFAULT_LOCALE;
  if (!locale) parsed = DEFAULT_LOCALE;
  else if (locale.startsWith("en")) parsed = "en-CH";
  else if (locale.startsWith("de")) parsed = "de";
  else if (locale.startsWith("fr")) parsed = "fr";
  else if (locale.startsWith("it")) parsed = "it";
  return supportedLocales.includes(parsed) ? parsed : DEFAULT_LOCALE;
};

const APP_SUPPORTED_LOCALES =
  SUPPORTED_LOCALES_BY_APP[sharedEnvironment.appName] ?? AvailableLocales;
const DEFAULT_LANGUAGE: AppLocale = getLocaleFromNavigator();
const PARSED_DEFAULT_LANGUAGE = parseDefaultLocale(DEFAULT_LANGUAGE, APP_SUPPORTED_LOCALES);

export const useLocaleStore = create<LocaleStore>()(
  persist(
    set => ({
      locale: PARSED_DEFAULT_LANGUAGE,
      setLocale: (newLocale: AppLocale) =>
        set({
          locale: parseDefaultLocale(newLocale, APP_SUPPORTED_LOCALES),
        }),
    }),
    {
      storage: createJSONStorage(() => localStorage),
      version: 2,
      name: "locale",
    },
  ),
);
